import { Text, type UseModalState } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, FullScreenDialog } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { type ShiftFormattedPayBreakdown } from "@src/appV2/redesign/Shift/types";
import { WorkerShiftCard } from "@src/appV2/redesign/Shift/WorkerShift/Card";
import { formatDayOfMonthWithDayName } from "@src/appV2/redesign/utils/formatDayOfMonthWithDayName";
import { shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { parseISO } from "date-fns";

import { type GetWorkerShiftResponse } from "../../api/useGetWorkerShift";

interface ConfirmAttendanceBottomSheetContentProps {
  modalState: UseModalState;
  shift: GetWorkerShiftResponse["response"];
  qualification: string;
  arrivalTime: string;
  isShiftToday: boolean;
  cancellationTime: string;
  shiftStartDate: string;
  shiftEndDate: string;
  payBreakdown?: ShiftFormattedPayBreakdown;
  onCancelShift: () => void;
  onConfirmAttendance: () => void;
}

export function ConfirmAttendanceBottomSheetContent(
  props: ConfirmAttendanceBottomSheetContentProps
) {
  const {
    modalState,
    arrivalTime,
    shift,
    qualification,
    isShiftToday,
    cancellationTime,
    shiftStartDate,
    shiftEndDate,
    payBreakdown,
    onCancelShift,
    onConfirmAttendance,
  } = props;

  useLogEffect(APP_V2_USER_EVENTS.ATTENDANCE_CONFIRMATION_NUDGE_VIEWED, {
    workerId: shift.agentId,
    shiftId: shift._id,
  });

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={8}
          sx={{
            paddingTop: 10,
            paddingX: 8,
            textAlign: "center",
          }}
        >
          <Text variant="h3">
            Will you be there at{" "}
            <Text noWrap inline variant="h3">
              {arrivalTime}
            </Text>{" "}
            {isShiftToday ? "today" : "tomorrow"}?
          </Text>
          <Text>
            Have your plans changed? Cancel before{" "}
            <Text inline bold color={(theme) => theme.palette.intent?.error.text}>
              {cancellationTime}
            </Text>{" "}
            to prevent an account{" "}
            <Text inline bold color={(theme) => theme.palette.intent?.error.text}>
              Restriction
            </Text>
            .
          </Text>
          <Stack
            spacing={4}
            sx={{
              textAlign: "left",
            }}
          >
            <Text bold variant="h5">
              {formatDayOfMonthWithDayName(parseISO(shiftStartDate))}
            </Text>
            <WorkerShiftCard
              workplaceId={shift.facility.userId ?? ""}
              workplaceName={shift.facility.name}
              workplaceTimezone={shift.facility.tmz ?? ""}
              workplaceRequiresLunchBreak={shift.facility.requiresLunchBreak ?? false}
              shiftTimeSlot={shiftPeriodToTimeSlot[shift.name]}
              shiftStart={shiftStartDate}
              shiftEnd={shiftEndDate}
              shiftQualificationName={qualification}
              shiftHourlyPay={payBreakdown?.hourlyPay}
              shiftTotalPay={payBreakdown?.totalPay}
              shiftTotalWithBoostPay={payBreakdown?.totalWithBoostPay}
            />
          </Stack>
        </Stack>
      </Box>
      <DialogFooter orientation="vertical">
        <Button
          fullWidth
          variant="contained"
          size="large"
          data-testid="confirm-attendance-button"
          onClick={onConfirmAttendance}
        >
          Yes, I&apos;ll be there
        </Button>
        <Button
          fullWidth
          variant="destructive"
          size="large"
          data-testid="cancel-shift-button"
          onClick={onCancelShift}
        >
          No, cancel my shift
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
